import { useModalOpenAppContext, useSetModalStatusMainAppContext } from '~/contexts/MainAppContext'

import ModalOverlay from '../ModalOverlay'
import { ChangeEvent, useState } from 'react'
import { Button } from '~/components/general/Button'
import { Input } from '~/components/utils/Input'
import { Link } from '@remix-run/react'
import classNames from 'classnames'
import { superAppApiFetch } from '~/utils/superAppApiFetch'
import { superAppApi } from '~/queries/routes'
import { queryClient } from '~/libs/tanstack/client'
import { CustomError } from '~/types/error'
import { checkValidEmail } from '~/utils/checkEmail'
import { trackEvent } from '~/utils/tracking'

type ErrorMessageProps = {
  emailErrorMessage: string
  fileErrorMessage: string
  otherErrorMessage: string
  userExistsError: boolean
}

type UserExistsError = {
  success: boolean
  error: string
  userExists?: string
  user_exists?: string
}

const UploadV2 = () => {
  const modalOpen = useModalOpenAppContext()
  const setModalStatus = useSetModalStatusMainAppContext()

  const handleOnClose = () => {
    trackEvent('click_exit_upload_modal')
    setModalStatus(false)
  }

  const [uploadedFile, setUploadedFile] = useState<File | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [email, setEmail] = useState<string>('')
  const [isDragOver, setIsDragOver] = useState<boolean>(false)
  const [errors, setErrors] = useState<ErrorMessageProps>({
    emailErrorMessage: '',
    fileErrorMessage: '',
    otherErrorMessage: '',
    userExistsError: false,
  })

  const handleFileUpload = (file: File) => {
    if (
      file &&
      (file.type === 'application/pdf' ||
        file.type === 'application/msword' ||
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')
    ) {
      trackEvent('upload_resume_success')
      if (file.size <= 10 * 1024 * 1024) {
        // 10MB limit
        setErrors({ ...errors, fileErrorMessage: '' })
        setUploadedFile(file)
      } else {
        setErrors({ ...errors, fileErrorMessage: 'File size must be less than 10MB' })
      }
    } else {
      setErrors({ ...errors, fileErrorMessage: 'Please upload a DOC, DOCX, or PDF file' })
    }
  }

  const clearErrors = () => {
    setErrors((current) => ({
      ...current,
      emailErrorMessage: '',
      fileErrorMessage: '',
      otherErrorMessage: '',
      userExistsError: false,
    }))
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(false)
    const file = e.dataTransfer?.files[0]
    if (file) handleFileUpload(file)
  }

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target?.files && e.target.files[0]
    if (file) handleFileUpload(file)
  }

  const handleUpload = async (event: React.SyntheticEvent) => {
    event.preventDefault()
    if (!uploadedFile) return

    try {
      clearErrors()
      setIsLoading(true)

      const isEmailValid = checkValidEmail(email)
      if (!isEmailValid) return setErrors({ ...errors, emailErrorMessage: 'Invalid email' })

      const registerResponse = await superAppApiFetch(
        superAppApi.register,
        {
          bodyData: { email },
          method: 'POST',
        },
        true,
      )

      if (!registerResponse.success || !registerResponse.token) return
      localStorage.setItem('API_ACCESS_TOKEN', registerResponse.token)

      await queryClient.invalidateQueries({ queryKey: ['user'] })

      const form = new FormData()
      form.append('resume', uploadedFile)

      if (email) form.append('email', email)

      trackEvent('submit_sign_up_upload')
      const uploadResumeResponse = await superAppApiFetch(
        superAppApi.uploadAndParse,
        {
          body: form,
          method: 'POST',
        },
        true,
      )

      if (!uploadResumeResponse.success) return
      trackEvent('upload_resume_success')
      window.location.replace(uploadResumeResponse.redirectUrl)

      setModalStatus(false)
    } catch (e: unknown) {
      if (e instanceof Error) {
        const errorMessage = JSON.parse(e.message as string) as CustomError

        const bodyMessage = errorMessage.body as UserExistsError
        trackEvent('upload_resume_error', {
          error: bodyMessage.error || '',
        })

        if (
          !bodyMessage.success &&
          bodyMessage.error === 'User already exists' &&
          (bodyMessage.userExists || bodyMessage.user_exists)
        ) {
          setErrors({ ...errors, userExistsError: true, emailErrorMessage: '' })
          return
        }

        setErrors({ ...errors, otherErrorMessage: 'Something went wrong on our side' })
      } else {
        trackEvent('upload_resume_error', {
          error: ((e as Error).message as string) || '',
        })
        console.log(e)
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      {modalOpen && (
        <ModalOverlay onClose={handleOnClose}>
          <div
            className="max-w-2xl mx-auto py-12 px-9 bg-white text-black relative flex flex-col gap-3 rounded-xl max-md:px-6"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              id="file-input"
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx"
              onChange={handleFileSelect}
            />
            <div
              className="absolute w-7 h-7 rounded-[28px] max-md:top-4 max-md:right-4 top-8 right-8 bg-neutral-40 before:text-neutral-60 after:text-neutral-60 after:bg-neutral-40 before:bg-neutral-40 bg-opacity-30 cursor-pointer after:rotate-45 before:content-[''] after:content-[''] before:-rotate-45  before:h-[2px] after:h-[2px] before:w-3 after:w-3 flex justify-center items-center z-100 before:absolute after:absolute"
              onClick={handleOnClose}
            />
            <div className="relative bg-white">
              <h1 className="text-mobile-xl text-black text-center mb-2">Upload resume</h1>
              <p
                className={classNames('text-neutral-50 text-center text-mobile-s', {
                  'text-red-50': errors.fileErrorMessage,
                })}
              >
                DOC, DOCX, or PDF (max 10MB)
              </p>
            </div>

            <div
              className={`text-center transition-colors cursor-pointer ${
                isDragOver ? 'bg-blue-50' : 'hover:bg-gray-50'
              }`}
              onDragOver={(e) => {
                e.preventDefault()
                setIsDragOver(true)
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={(e) => handleDrop(e)}
              onClick={() => document?.getElementById('file-input')?.click()}
            >
              {!uploadedFile ? (
                <Button type="button" text="Browse file" className="w-full flex justify-center" />
              ) : (
                <div className="flex justify-center gap-1 px-3 bg-green-50 text-white text-mobile-s rounded-[100px] py-4 break-all">
                  <img
                    className="my-auto object-cover w-full h-full max-w-6 max-h-6"
                    src={'/icons/green-check.svg'}
                    alt={'done'}
                  />
                  <p className="text-left">{uploadedFile.name}</p>
                </div>
              )}
            </div>

            <Input
              name={'email'}
              label="Please share your email address to continue."
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              placeholder="no.spam.never@success.com"
            />
            {errors.userExistsError && (
              <span className="text-red-50 text-left text-mobile-xs">
                This email already exists.
                <br />
                <a className="underline" href="/app/auth/sign-in">
                  Click here to sign in
                </a>{' '}
                or check your email for the magic link
              </span>
            )}
            {(errors.emailErrorMessage || errors.otherErrorMessage) && (
              <span className="text-red-50 text-left text-mobile-xs">
                {errors.emailErrorMessage || errors.otherErrorMessage}
              </span>
            )}
            {/* Terms and Submit Section */}
            <div className="flex justify-between items-center gap-6 max-md:flex-col max-md:gap-3">
              <div className="text-sm text-neutral-50 max-md:text-center">
                <p>By sharing your email you agree</p>
                <p>
                  with our{' '}
                  <Link to={'/tos'} target="_blank" className="text-blue-50 hover:text-blue-700">
                    Terms of Use
                  </Link>{' '}
                  and{' '}
                  <Link
                    to={'/privacy'}
                    target="_blank"
                    className="text-blue-50 hover:text-blue-700"
                  >
                    Privacy Policy
                  </Link>
                </p>
              </div>
              <Button
                type="button"
                color={!uploadedFile || !email ? 'gray' : 'blue'}
                className="md:w-7/12 max-md:w-full justify-center"
                onClick={handleUpload}
                loading={isLoading}
              >
                Improve resume
              </Button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </>
  )
}

export default UploadV2
